import { useState, useCallback, useMemo } from 'react';
import { useAppDispatch } from '../redux/hooks';
import { updateQuantity } from '../redux/slices/cartSlice';
import { MenuItem } from '../redux/slices/menuSlice';
import { findCartItemVariants, hasCustomizations } from '../utils/cartUtils';

/**
 * Custom hook for managing cart quantity controls with smart customization logic
 * Handles increment/decrement with RepeatCustomisationModal and DeleteVariantModal
 */
export const useCartQuantityControls = (
  cartItems: any[],
  handleAddToCart: (item: MenuItem) => void,
  openModal: (item: MenuItem) => void
) => {
  const dispatch = useAppDispatch();

  // Smart Cart Modal States
  const [isRepeatCustomizationModalOpen, setIsRepeatCustomizationModalOpen] = useState(false);
  const [itemToRepeatOrCustomize, setItemToRepeatOrCustomize] = useState<any>(null);
  const [isDeleteVariantModalOpen, setIsDeleteVariantModalOpen] = useState(false);
  const [itemIdForDelete, setItemIdForDelete] = useState<string>('');
  const [itemNameForDelete, setItemNameForDelete] = useState<string>('');

  // Recompute variants from current cart items (reactive to cart changes)
  const variantsToDelete = useMemo(() => {
    if (!itemIdForDelete) return [];
    return findCartItemVariants(itemIdForDelete, cartItems);
  }, [itemIdForDelete, cartItems]);

  // Simple quantity update
  const handleUpdateQuantity = useCallback((cartItemId: string, quantity: number) => {
    dispatch(updateQuantity({ cartItemId, quantity }));
  }, [dispatch]);

  // SMART CART INCREMENT HANDLER
  const handleIncrementItemQuantity = useCallback((item: MenuItem) => {
    const variants = findCartItemVariants(item.pk_id.toString(), cartItems);
    
    if (variants.length > 0) {
      // Item exists in cart - check for customizations
      const customizedVariants = variants.filter(v => hasCustomizations(v));
      
      if (customizedVariants.length > 0) {
        // Get the LAST (most recently added) customized variant
        const variantWithCustomization = customizedVariants[customizedVariants.length - 1];
        
        // Show repeat modal with the most recent customized variant
        setItemToRepeatOrCustomize(variantWithCustomization);
        setIsRepeatCustomizationModalOpen(true);
      } else {
        // No customization - just increment the first variant
        dispatch(updateQuantity({ 
          cartItemId: variants[0].cartItemId, 
          quantity: variants[0].quantity + 1 
        }));
      }
    } else {
      // Not in cart - use existing add to cart logic
      handleAddToCart(item);
    }
  }, [cartItems, dispatch, handleAddToCart]);

  // SMART CART DECREMENT HANDLER
  const handleDecrementItemQuantity = useCallback((itemId: string) => {
    const variants = findCartItemVariants(itemId, cartItems);
    
    if (variants.length > 1) {
      // Multiple variants - show delete modal to choose which one
      const item = variants[0].item;
      setItemNameForDelete(item.name);
      setItemIdForDelete(itemId);
      setIsDeleteVariantModalOpen(true);
    } else if (variants.length === 1) {
      // Single variant - direct decrement
      const variant = variants[0];
      dispatch(updateQuantity({ 
        cartItemId: variant.cartItemId, 
        quantity: variant.quantity - 1 
      }));
    }
  }, [cartItems, dispatch]);

  // REPEAT CUSTOMIZATION MODAL HANDLERS
  const handleRepeatPreviousCustomization = useCallback(() => {
    if (itemToRepeatOrCustomize) {
      // Increment quantity of the same variant
      dispatch(updateQuantity({ 
        cartItemId: itemToRepeatOrCustomize.cartItemId, 
        quantity: itemToRepeatOrCustomize.quantity + 1 
      }));
      setIsRepeatCustomizationModalOpen(false);
      setItemToRepeatOrCustomize(null);
    }
  }, [itemToRepeatOrCustomize, dispatch]);

  const handleCustomizeNewVariant = useCallback(() => {
    if (itemToRepeatOrCustomize) {
      // Open AddToCartModal for new customization
      openModal(itemToRepeatOrCustomize.item);
      setIsRepeatCustomizationModalOpen(false);
      setItemToRepeatOrCustomize(null);
    }
  }, [itemToRepeatOrCustomize, openModal]);

  // CLOSE MODAL HANDLERS
  const closeRepeatModal = useCallback(() => {
    setIsRepeatCustomizationModalOpen(false);
    setItemToRepeatOrCustomize(null);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setIsDeleteVariantModalOpen(false);
    setItemIdForDelete('');
    setItemNameForDelete('');
  }, []);

  return {
    // Modal State
    isRepeatCustomizationModalOpen,
    itemToRepeatOrCustomize,
    isDeleteVariantModalOpen,
    variantsToDelete,
    itemNameForDelete,
    
    // Quantity Control Handlers
    handleUpdateQuantity,
    handleIncrementItemQuantity,
    handleDecrementItemQuantity,
    
    // Modal Action Handlers
    handleRepeatPreviousCustomization,
    handleCustomizeNewVariant,
    
    // Close Handlers
    closeRepeatModal,
    closeDeleteModal,
  };
};

export default useCartQuantityControls;
